import type { SortType } from "@/features/catalog/stores/catalogStore";
import type { SearchScope } from "@/features/search/searchScope";
import type {
  EventDispatchResult,
  TypedEvent,
} from "@/shared/lib/componentHierarchy";

export type CatalogEvent =
  | { readonly type: "catalog/search-query-changed"; readonly query: string }
  | { readonly type: "catalog/search-cleared" }
  | {
      readonly type: "catalog/search-scope-changed";
      readonly scope: SearchScope;
    }
  | { readonly type: "catalog/search-submitted" }
  | {
      readonly type: "catalog/search-synchronized";
      readonly query: string;
      readonly scope: SearchScope;
    }
  | {
      readonly type: "catalog/tags-synchronized";
      readonly selectedTag: string | null;
      readonly hiddenTags: readonly string[];
    }
  | { readonly type: "catalog/tag-selection-toggled"; readonly tag: string }
  | { readonly type: "catalog/tag-selection-cleared" }
  | { readonly type: "catalog/tag-visibility-toggled"; readonly tag: string }
  | { readonly type: "catalog/hidden-tags-cleared" }
  | { readonly type: "catalog/sort-selected"; readonly sort: SortType }
  | { readonly type: "catalog/page-selected"; readonly page: number }
  | { readonly type: "catalog/auto-reload-changed"; readonly enabled: boolean }
  | { readonly type: "catalog/refresh-requested"; readonly manual: boolean }
  | { readonly type: "catalog/retry-requested" }
  | { readonly type: "catalog/load-more-requested" }
  | { readonly type: "catalog/sidebar-toggle-requested" }
  | {
      readonly type: "catalog/scroll-requested";
      readonly edge: "top" | "bottom";
    }
  | {
      readonly type: "catalog/settings-link-clicked";
      readonly hasModifier: boolean;
    }
  | { readonly type: "catalog/settings-close-requested" }
  | { readonly type: "catalog/sort-modal-open-requested" }
  | { readonly type: "catalog/sort-modal-close-requested" }
  | {
      readonly type: "catalog/sort-modal-display-changed";
      readonly displayType: "catalog" | "list";
    }
  | {
      readonly type: "catalog/sort-modal-sort-selected";
      readonly sort: SortType;
    }
  | { readonly type: "catalog/tag-modal-open-requested" }
  | { readonly type: "catalog/tag-modal-close-requested" }
  | { readonly type: "catalog/list-view-requested" }
  | { readonly type: "catalog/thread-create-open-requested" }
  | { readonly type: "catalog/thread-create-close-requested" }
  | { readonly type: "catalog/thread-create-toggle-requested" }
  | {
      readonly type: "catalog/page-model-synchronized";
      readonly isDesktop: boolean;
      readonly isThreadCreateOpen: boolean;
      readonly isFetching: boolean;
      readonly hasNextPage: boolean;
    }
  | {
      readonly type: "catalog/viewport-synchronized";
      readonly isDesktop: boolean;
    }
  | { readonly type: "catalog/toolbar-filter-toggled" }
  | {
      readonly type: "catalog/item-synchronized";
      readonly threadId: string;
      readonly boardSlug: string;
      readonly isNg: boolean;
      readonly isR18: boolean;
      readonly hasAttachment: boolean;
      readonly showR18: boolean;
      readonly isViewed: boolean;
      readonly trackViewed: boolean;
      readonly prefetchAvailable: boolean;
      readonly menuOpenMethod: "auto" | "long-press" | "menu-button" | "off";
      readonly hasFinePointer: boolean;
    }
  | { readonly type: "catalog/item-hover-changed"; readonly hovered: boolean }
  | { readonly type: "catalog/item-menu-opened" }
  | { readonly type: "catalog/item-menu-closed" }
  | { readonly type: "catalog/item-menu-toggled" }
  | { readonly type: "catalog/item-ng-revealed" }
  | { readonly type: "catalog/item-r18-revealed" }
  | { readonly type: "catalog/item-activated" }
  | { readonly type: "catalog/item-prefetch-requested" }
  | { readonly type: "catalog/context-delete-requested" }
  | { readonly type: "catalog/context-hide-requested" }
  | { readonly type: "catalog/context-show-requested" }
  | { readonly type: "catalog/context-ng-title-requested" }
  | { readonly type: "catalog/context-ng-image-requested" }
  | { readonly type: "catalog/context-history-remove-requested" }
  | { readonly type: "catalog/context-close-requested" }
  | {
      readonly type: "catalog/layout-initialization-requested";
      readonly isDesktop: boolean;
    };

const CATALOG_EVENT_TYPES = new Set<CatalogEvent["type"]>([
  "catalog/search-query-changed",
  "catalog/search-cleared",
  "catalog/search-scope-changed",
  "catalog/search-submitted",
  "catalog/search-synchronized",
  "catalog/tags-synchronized",
  "catalog/tag-selection-toggled",
  "catalog/tag-selection-cleared",
  "catalog/tag-visibility-toggled",
  "catalog/hidden-tags-cleared",
  "catalog/sort-selected",
  "catalog/page-selected",
  "catalog/auto-reload-changed",
  "catalog/refresh-requested",
  "catalog/retry-requested",
  "catalog/load-more-requested",
  "catalog/sidebar-toggle-requested",
  "catalog/scroll-requested",
  "catalog/settings-link-clicked",
  "catalog/settings-close-requested",
  "catalog/sort-modal-open-requested",
  "catalog/sort-modal-close-requested",
  "catalog/sort-modal-display-changed",
  "catalog/sort-modal-sort-selected",
  "catalog/tag-modal-open-requested",
  "catalog/tag-modal-close-requested",
  "catalog/list-view-requested",
  "catalog/thread-create-open-requested",
  "catalog/thread-create-close-requested",
  "catalog/thread-create-toggle-requested",
  "catalog/page-model-synchronized",
  "catalog/viewport-synchronized",
  "catalog/toolbar-filter-toggled",
  "catalog/item-synchronized",
  "catalog/item-hover-changed",
  "catalog/item-menu-opened",
  "catalog/item-menu-closed",
  "catalog/item-menu-toggled",
  "catalog/item-ng-revealed",
  "catalog/item-r18-revealed",
  "catalog/item-activated",
  "catalog/item-prefetch-requested",
  "catalog/context-delete-requested",
  "catalog/context-hide-requested",
  "catalog/context-show-requested",
  "catalog/context-ng-title-requested",
  "catalog/context-ng-image-requested",
  "catalog/context-history-remove-requested",
  "catalog/context-close-requested",
  "catalog/layout-initialization-requested",
]);

export function isCatalogEvent(event: TypedEvent): event is CatalogEvent {
  return CATALOG_EVENT_TYPES.has(event.type as CatalogEvent["type"]);
}

export type CatalogEventNotifier = (
  event: CatalogEvent,
) => EventDispatchResult<TypedEvent>["status"];
